/**
 * Dialog used by a user to file a report against another user.
 *
 * Captures category + free-text description and (optionally) the message id
 * that triggered the report. Submits to POST /api/partner/reports.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { submitReport, type ReportCategory } from '@/lib/moderationApi';

interface ReportUserDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly reportedUserId: string;
  readonly reportedUserName: string;
  readonly messageId?: string;
  readonly requestId?: string;
  readonly onSubmitted?: () => void;
}

const CATEGORY_KEYS: ReportCategory[] = [
  'harassment',
  'spam',
  'sexual_content',
  'hate_speech',
  'minor_safety',
  'scam_or_fraud',
  'impersonation',
  'self_harm',
  'other',
];

export function ReportUserDialog({
  open,
  onOpenChange,
  reportedUserId,
  reportedUserName,
  messageId,
  requestId,
  onSubmitted,
}: ReportUserDialogProps) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<ReportCategory>('harassment');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory('harassment');
    setDescription('');
    setSubmitting(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error(
        t('moderation.report.errorDescription', {
          defaultValue: 'Please describe what happened.',
        }),
      );
      return;
    }
    setSubmitting(true);
    const id = await submitReport({
      reportedUserId,
      category,
      description: description.trim(),
      messageId,
      requestId,
    });
    setSubmitting(false);
    if (id) {
      toast.success(
        t('moderation.report.success', {
          defaultValue: 'Report submitted. Our team will review it.',
        }),
      );
      onSubmitted?.();
      handleClose(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('moderation.report.title', {
              defaultValue: 'Report {{name}}',
              name: reportedUserName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('moderation.report.description', {
              defaultValue:
                'Reports are sent to our moderation team. Encrypted message content remains private; only your description and the report metadata are reviewed.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="report-category">
              {t('moderation.report.categoryLabel', { defaultValue: 'Category' })}
            </Label>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as ReportCategory)}
            >
              <SelectTrigger id="report-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`moderation.report.categories.${key}`, {
                      defaultValue: key.replace(/_/g, ' '),
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-description">
              {t('moderation.report.descriptionLabel', { defaultValue: 'What happened?' })}
            </Label>
            <Textarea
              id="report-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={5}
              placeholder={t('moderation.report.descriptionPlaceholder', {
                defaultValue:
                  'Please give as much detail as you can. Do not include passwords or other secrets.',
              })}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/2000
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleClose(false)}
            disabled={submitting}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? t('moderation.report.submitting', { defaultValue: 'Submitting…' })
              : t('moderation.report.submit', { defaultValue: 'Submit report' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
